import { isWithinSchedule } from '@theme/notice-schedule';

/** Refresh server-rendered visibility after caching, navigation, or section replacement. */
class StoreNotice extends HTMLElement {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  #observer = new MutationObserver(() => this.#refresh());

  connectedCallback() {
    this.#refresh();
    // Horizon morphs this element in place, including cached visibility attributes.
    this.#observer.observe(this, {
      attributes: true,
      attributeFilter: ['hidden', 'data-design-mode', 'data-schedule-valid', 'data-start-timestamp', 'data-end-timestamp'],
    });
    document.addEventListener('visibilitychange', this.#refresh);
    window.addEventListener('pageshow', this.#refresh);
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#observer.disconnect();
    document.removeEventListener('visibilitychange', this.#refresh);
    window.removeEventListener('pageshow', this.#refresh);
  }

  #refresh = () => {
    clearTimeout(this.#timer);
    if (this.dataset.designMode === 'true') return;

    const now = Date.now() / 1000;
    const hidden = !isWithinSchedule(this, now);
    // Avoid observing our own unchanged visibility writes indefinitely.
    if (this.hidden !== hidden) this.hidden = hidden;
    if (this.dataset.scheduleValid === 'false') return;

    const nextBoundary = [this.dataset.startTimestamp, this.dataset.endTimestamp]
      .filter(Boolean)
      .map(Number)
      .filter((timestamp) => Number.isFinite(timestamp) && timestamp > now)
      .sort((a, b) => a - b)[0];

    if (nextBoundary !== undefined) {
      this.#timer = setTimeout(this.#refresh, Math.min((nextBoundary - now) * 1000, 2147483647));
    }
  };
}

if (!customElements.get('store-notice')) customElements.define('store-notice', StoreNotice);
